import { AlertaCookie } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { AuthContext } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import {
  useGoogleAnalytics,
  useGoogleTagManager,
  useHotjar,
  useIndecx,
  useSentry,
} from '@4beefopag/4bee-fopag-core/dist/shared/ui/analytics';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import * as moment from 'moment';
import 'moment/locale/pt-br';
import React, { useMemo, useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import NotFound from './components/pages/NotFound';
import AuthErrorInterceptor from './AuthErrorInterceptor';
import Notificacoes from './components/atoms/Notificacoes';
import SidebarMenu from './components/organisms/SidebarMenu';
import { getAuthorizedRoutes, getDefaultUrl } from './constants/acl-config';
import indexcConfig from './constants/indexc.config';
import { useAnalytics } from './services/analytics';
import { useUserPolicy } from './user-policy'; // <-- Importação limpa

moment.locale('pt-BR');

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: true } },
});

function Router() {
  const location = useLocation();
  const navigate = useNavigate();
  const { identifyUser } = useAnalytics();

  const { isGestor, isAdmin, userAccount, loading } = useUserPolicy();
  const memoizedAuthContext = useMemo(() => ({ isGestor, isAdmin }), [isGestor, isAdmin]);

  const allowedRoutes = useMemo(() => (userAccount && getAuthorizedRoutes(userAccount.ability)) || [], [userAccount]);

  const [aceitouCookie, setAceitouCookie] = useState(undefined);
  const [indecxConfig, setIndecxConfig] = useState();

  useHotjar(aceitouCookie, process.env.REACT_APP_HOTJAR_ID, process.env.REACT_APP_HOTJAR_VERSION);
  useGoogleAnalytics(aceitouCookie, process.env.REACT_APP_GA_ID);
  useGoogleTagManager(aceitouCookie, process.env.REACT_APP_GTM_ID);
  useSentry(process.env.REACT_APP_SENTRY_DNS, process.env.REACT_APP_COOP_NAME, {
    ignoreErrors: ['Unauthorized', 'Forbidden', /401/, /403/],
    release: process.env.REACT_APP_VERSION,
  });
  useIndecx(indexcConfig.feedback_gestor, indecxConfig);

  const redirectToDefaultRoute = () => {
    if (location.pathname !== '/') {
      return;
    }

    const defaultUrl = getDefaultUrl(userAccount.ability);
    navigate(defaultUrl);
  };

  useEffect(() => {
    if (userAccount?.username) {
      identifyUser({
        username: userAccount.username,
        email: userAccount.email,
        name: userAccount.name,
      });
    }
  }, [userAccount, identifyUser]);

  useEffect(() => {
    if (userAccount && userAccount.ability) {
      redirectToDefaultRoute();
    }
    if (userAccount && userAccount.companies && userAccount.isCustomer) {
      setIndecxConfig({
        name: userAccount.name,
        email: userAccount.email,
        phone: userAccount.phone?.replace(/[ -+]/g, ''),
        indicators: {
          CSM: userAccount.companies[0]?.carteiraFolha || '',
          BU: 'Cooperativa',
          username: userAccount.username.trim().toUpperCase(),
          folhas: userAccount.companies.map((folha) => `${folha.id} ${folha.name}`),
        },
      });
    }
  }, [userAccount]);

  if (loading || !userAccount) {
    return <Skeleton />;
  }

  return (
    <>
      <AuthContext.Provider value={memoizedAuthContext}>
        <AuthErrorInterceptor>
          {/* UserPolicyContext.Provider removido daqui, pois agora ele envolve a aplicação inteira */}
          <SidebarMenu userAccount={userAccount} />
          <Notificacoes />
          <div id="container">
            <QueryClientProvider client={queryClient}>
              <Routes>
                {allowedRoutes.map(({ path, exact, element }) => (
                  <Route
                    key={path}
                    path={path}
                    exact={exact}
                    element={element}
                  />
                ))}
                <Route
                  path="/not-found"
                  exact
                  element={<NotFound />}
                />
                <Route
                  path="*"
                  element={<Navigate to="/not-found" />}
                />
              </Routes>
            </QueryClientProvider>
          </div>
        </AuthErrorInterceptor>
      </AuthContext.Provider>
      <AlertaCookie
        cookieName="cookie-coop"
        onAccept={() => setAceitouCookie(true)}
      />
    </>
  );
}

export default Router;
